import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Clock, TriangleAlert, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
         DialogTitle } from "@/components/ui/dialog";
import { api } from "../lib/api";
import { useAuth } from "@/lib/AuthContext";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const STATUS_TONE = {
  "Not Ordered": "bg-red-100 text-red-800",
  "Not On Hand": "bg-amber-100 text-amber-900",
};

/**
 * FLD-5 — everything a room is still waiting on. The follow-up tickets write
 * themselves the moment material lands, so the only thing to do here is read it
 * (or cancel a follow-up nobody wants).
 */
export default function WaitingPanel({ projectId, onOpenTicket }) {
  const { user } = useAuth();
  const actor = user?.name || user?.email || "";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancel, setCancel] = useState(null);      // { number, id, reason }

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/material/waiting", { params: { project_id: projectId } });
      setData(d);
    } catch (e) { toast.error(errText(e, "Could not read the waiting list")); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const recheck = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/material/waiting/rematch",
        { project_id: projectId, actor });
      toast.success(res.matched > 0
        ? `${res.matched} unit(s) matched — ${res.tickets.length} follow-up ticket(s) created`
        : "Nothing on hand matches what is waiting");
      await load();
    } catch (e) { toast.error(errText(e, "Could not re-check")); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/material/follow-ups/${cancel.id}/cancel`,
        { by: actor, reason: cancel.reason });
      toast.success(`${res.ticket_number} cancelled`, {
        description: `${res.restored_to_waiting} unit(s) went back to waiting`,
      });
      setCancel(null);
      await load();
    } catch (e) { toast.error(errText(e, "Could not cancel the follow-up")); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  }
  const { rows, summary } = data;

  return (
    <div className="space-y-2" data-testid="waiting-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-gray-600" data-testid="waiting-summary">
          <b className="text-gray-900 tabular-nums">{summary.waiting}</b> unit(s) waiting
          {summary.not_ordered > 0 && (
            <span className="text-red-700"> · {summary.not_ordered} not ordered</span>
          )}
          {summary.eta_passed > 0 && (
            <span className="text-red-700"> · {summary.eta_passed} past their delivery date</span>
          )}
          {summary.followed_up > 0 && (
            <span className="text-blue-700"> · {summary.followed_up} received and on a follow-up</span>
          )}
        </span>
        <span className="text-[10px] text-gray-400">
          Follow-up tickets are created automatically the moment material is received
        </span>
        <Button size="sm" variant="outline" className="h-7 ml-auto" disabled={busy}
                onClick={recheck} data-testid="waiting-recheck">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Re-check receipts
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500"
             data-testid="waiting-empty">
          Nothing is waiting on material.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="waiting-rows">
            <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left p-2">Room</th>
                <th className="text-left p-2">Product</th>
                <th className="text-right p-2">Waiting</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">PO / ETA</th>
                <th className="text-left p-2">Needed by</th>
                <th className="text-left p-2">Follow-up</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.line_id} className="border-t border-gray-100"
                    data-testid={`waiting-row-${r.line_id}`}>
                  <td className="p-2">{r.room}</td>
                  <td className="p-2 font-mono">{r.product_short}</td>
                  <td className="p-2 text-right tabular-nums font-semibold"
                      data-testid={`waiting-qty-${r.line_id}`}>{r.waiting}</td>
                  <td className="p-2">
                    <span className={`px-1 rounded text-[10px] font-semibold ${
                      STATUS_TONE[r.line_status] || "bg-gray-100 text-gray-600"}`}>
                      {r.line_status}
                    </span>
                  </td>
                  <td className="p-2">
                    {r.po_ref || <span className="text-gray-400">no PO</span>}
                    {r.direct_ship && (
                      <span title="This PO ships straight to site — it never comes through the shop"
                            data-testid={`waiting-direct-ship-${r.line_id}`}
                            className="ml-1 text-[9px] uppercase font-semibold px-1 rounded bg-sky-100 text-sky-800">
                        ships to site
                      </span>
                    )}
                    {r.expected_arrival && (
                      <span className={r.eta_passed ? "text-red-700 font-semibold ml-1" : "text-gray-500 ml-1"}
                            data-testid={`waiting-eta-${r.line_id}`}>
                        {r.eta_passed && <TriangleAlert className="w-3 h-3 inline mr-0.5" />}
                        ETA {r.expected_arrival}
                      </span>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    {r.need_by || "—"}
                    {r.days_to_need_by !== null && r.days_to_need_by <= 5 && (
                      <span className="ml-1 text-red-700 font-semibold"
                            data-testid={`waiting-countdown-${r.line_id}`}>
                        <Clock className="w-3 h-3 inline" />{" "}
                        {r.days_to_need_by < 0 ? "overdue"
                          : r.days_to_need_by === 0 ? "today" : `in ${r.days_to_need_by}d`}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {r.followup_ticket_number ? (
                      <button className="text-blue-700 underline-offset-2 hover:underline font-mono"
                              data-testid={`waiting-followup-${r.line_id}`}
                              onClick={() => onOpenTicket?.(r.followup_ticket_number)}>
                        <Truck className="w-3 h-3 inline mr-0.5" />
                        {r.followup_ticket_number}
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {r.followup_ticket_number && r.followup_status === "Requested" && (
                      <button className="text-red-600" title="Cancel the follow-up ticket"
                              data-testid={`waiting-cancel-${r.line_id}`}
                              onClick={() => setCancel({ id: r.followup_ticket_id,
                                                         number: r.followup_ticket_number,
                                                         reason: "" })}>
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!cancel} onOpenChange={(v) => { if (!v) setCancel(null); }}>
        <DialogContent className="max-w-sm" data-testid="waiting-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Cancel <span className="font-mono">{cancel?.number}</span>?
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              The quantity goes back to waiting, so the next receipt picks it up again.
              The reason is logged.
            </DialogDescription>
          </DialogHeader>
          <Input value={cancel?.reason || ""} placeholder="Why is it being cancelled?"
                 data-testid="waiting-cancel-reason"
                 onChange={(e) => setCancel((c) => ({ ...c, reason: e.target.value }))} />
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setCancel(null)}
                    data-testid="waiting-cancel-keep">Keep it</Button>
            <Button size="sm" variant="destructive" disabled={busy || !cancel?.reason?.trim()}
                    onClick={doCancel} data-testid="waiting-cancel-confirm">
              Cancel the follow-up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
